import React, { useEffect, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface BulletinContentProps {
  content: string;
  size: 'small' | 'medium' | 'large';
  position?: 'center' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  hasImage?: boolean;
}

const BulletinContent = ({ content, size, position = 'center', hasImage = false }: BulletinContentProps) => {
  // Early return for empty or whitespace-only content
  if (!content || !content.trim()) {
    return null;
  }

  const textRef = useRef<HTMLParagraphElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout>();
  const currentFontSizeRef = useRef<number>(24);

  const adjustTextSize = useCallback(() => {
    if (!textRef.current || !containerRef.current) return;
    
    try {
      const container = containerRef.current;
      const text = textRef.current;
      
      // Reset to initial font size
      currentFontSizeRef.current = 24;
      text.style.fontSize = '24px';
      
      // Get container dimensions with padding
      const containerWidth = container.clientWidth - 48;
      const containerHeight = container.clientHeight - 48;
      
      // Prevent infinite loops with reasonable bounds
      let iterations = 0;
      const maxIterations = 20;
      
      // Adjust font size down if text overflows
      while (
        iterations < maxIterations &&
        (text.scrollWidth > containerWidth || text.scrollHeight > containerHeight) && 
        currentFontSizeRef.current > 12
      ) {
        currentFontSizeRef.current--;
        text.style.fontSize = `${currentFontSizeRef.current}px`;
        iterations++;
      }
      
      if (process.env.NODE_ENV === 'development' && iterations >= maxIterations) {
        console.warn('BulletinContent: Max font size adjustment iterations reached');
      }
    } catch (error) {
      console.error('BulletinContent: Error adjusting text size:', error);
    }
  }, []);

  const debouncedAdjustTextSize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    resizeTimeoutRef.current = setTimeout(adjustTextSize, 100);
  }, [adjustTextSize]);

  useEffect(() => {
    adjustTextSize();
    window.addEventListener('resize', debouncedAdjustTextSize);
    return () => {
      window.removeEventListener('resize', debouncedAdjustTextSize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
    // Only run on mount and resize, not on content changes to avoid scaling animation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjustTextSize, debouncedAdjustTextSize]);

  const getPositionClasses = () => {
    switch (position) {
      case 'top-left':
        return 'items-start justify-start text-left';
      case 'top-right':
        return 'items-start justify-end text-right';
      case 'bottom-left':
        return 'items-end justify-start text-left';
      case 'bottom-right':
        return 'items-end justify-end text-right';
      default:
        return 'items-center justify-center text-center';
    }
  };


  return (
    <div 
      ref={containerRef}
      className={cn(
        "absolute top-0 left-0 max-w-[60%] max-h-[40%] rounded-inherit",
        "flex p-4 bulletin-no-select z-50",
        getPositionClasses()
      )}
      style={{
        WebkitUserSelect: 'none',
        userSelect: 'none',
        WebkitTouchCallout: 'none',
        touchAction: 'none',
        contain: 'layout style' // CSS containment for performance
      }}
    >
      <p 
        ref={textRef}
        style={{ 
          fontSize: `${currentFontSizeRef.current}px`,
          WebkitUserSelect: 'none',
          userSelect: 'none',
          WebkitTouchCallout: 'none'
        }}
        className={cn(
          "font-medium leading-relaxed text-white",
          "max-w-[90%] bulletin-no-select",
          "drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]",
          position === 'top-left' && "drop-shadow-[0_2px_12px_rgba(0,0,0,0.9)]",
          size === 'large' && 'font-semibold',
          size === 'small' && 'font-normal'
        )}
      >
        {content}
      </p>
    </div>
  );
};

export default BulletinContent;